// externals
import type { DealHistory, StateTree } from '@makemydeal/dr-dash-types';

// types
import type { QuickCompareOffer } from '../types/quickCompareOffer';

// telemetry
import { deriveFieldKey, useTrackedSelector } from '../../telemetry/useTrackedSelector';
import type { TrackedSelectorOptions } from '../../telemetry/useTrackedSelector';

type DealArg = DealHistory | { offer?: QuickCompareOffer };

/**
 * Resolve a stable per-deal instance key so that a component rendered once per
 * comparison column does not share a dedup slot across columns. Undefined for
 * the primary deal (no `deal` argument), which reads from the live store.
 */
const dealInstanceKey = (deal?: DealArg): string | undefined =>
    (deal as { offer?: { offerId?: string } } | undefined)?.offer?.offerId;

/**
 * Build a deal-scoped `useSelector`. Reads from the supplied `deal` when given
 * (offer-compare columns and deal history), else from live store state.
 *
 * Telemetry (US2055264): this wraps `useTrackedSelector`, so every read through
 * it emits a change-only `SelectorRead`. That matters because the Deal Summary
 * money values — taxes, add-ons, rebates, net selling price, total of payments
 * — are ALL read through this hook, while plain `useSelector` in those
 * components is used only for feature toggles. Instrumenting here is what makes
 * the arithmetic observable rather than just the toggles.
 *
 * @param deal    deal to scope reads to; omit for the primary/live deal
 * @param component explicit component name for attribution. Required for usable
 *                  telemetry — the fiber-derived name resolves to `'unknown'` in
 *                  any production React build.
 *
 * The returned hook takes an optional explicit `field`. Supply it: the fallback
 * derives from the selector's function name, and DR-Dash selectors come from
 * published, already-minified packages, so derived keys arrive mangled and churn
 * on every dependency rebuild. Note the fallback deliberately derives from the
 * INNER `selector`, not the wrapper closure below — every call site shares that
 * closure's source text, so hashing it would collapse all reads onto one key.
 */
export const createDealRootSelector = (deal?: DealArg, component?: string) => {
    const instanceKey = dealInstanceKey(deal);
    return <T,>(selector: (state: StateTree) => T, field?: string, options?: TrackedSelectorOptions): T =>
        useTrackedSelector((state: StateTree) => selector(deal ? (deal as StateTree) : state), {
            ...options,
            component,
            field: field ?? deriveFieldKey(selector as (state: unknown) => unknown),
            instanceKey
        });
};
